<template>
  <div class="ihu-select-container">
    <label v-if="label" :for="name">
      {{ label }}
      <template v-if="tooltip">
        <img v-if="currentTheme === 'light'"
             v-tooltip="{ content: tooltip }"
             class="info-icon"
             :src="icInfoLT"
             alt="infoIcon">
        <img v-else
             v-tooltip="{ content: tooltip }"
             class="info-icon"
             :src="icInfoDT"
             alt="infoIcon">
      </template>
    </label>
    <div v-click-outside="onClickOutside"
         tabindex="0"
         :class="['select-wrapper', { 'is-open active': isListOpen }]">
      <IhuSelectDropdown
        v-if="isDropdown"
        tabindex="-1"
        :opened="isListOpen"
        :selectedItem="selectedItems[0]"
        :searchable="dropdownSearchable"
        :searchTerm="searchTerm"
        :disabled="disabled"
        :placeholder="placeholder"
        @toggle="toggle"
        @input="searchTermInput">
      </IhuSelectDropdown>
      <IhuSelectTags
        v-if="isMultiSelect"
        tabindex="-1"
        :opened="isListOpen"
        :itemIdAttr="itemIdAttr"
        :itemValueAttr="itemValueAttr"
        :tags="selectedItems"
        :searchTerm="searchTerm"
        :placeholder="placeholder"
        :placeholderIconClass="placeholderIconClass"
        :enabled="!disabled"
        @input="searchTermInput"
        @emitFocusEvent="toggle"
        @removeItem="setSelected">
      </IhuSelectTags>
      <IhuSelectList
        v-show="isListOpen && !disabled"
        :itemIdAttr="itemIdAttr"
        :itemValueAttr="itemValueAttr"
        :isMultiSelect="isMultiSelect"
        :items="filteredItems"
        :selectedItems="selectedItems"
        :isAllSelected="isAllSelected"
        @itemClicked="setSelected"
        @disabledItemClicked="disabledItemSelected"
        @selectAllItems="setAllSelected">
      </IhuSelectList>
    </div>
  </div>
</template>

<script>
  import { includes, values, filter } from 'lodash';
  import { currentThemeMixin } from '@/mixins';
  import icInfoDT from '@/components/common/assets/ic_info/ic_info_DT.svg';
  import icInfoLT from '@/components/common/assets/ic_info/ic_info_LT.svg';
  import { clickOutside } from '@/directives';
  import IhuSelectDropdown from './ihu-select-dropdown';
  import IhuSelectList from './ihu-select-list';
  import IhuSelectTags from './ihu-select-tags';

  const SELECT_TYPES = {
    MULTISELECT: 'multiselect',
    SINGLESELECT: 'singleselect',
    DROPDOWN: 'dropdown',
  };

  export default {
    name: 'ihu-select',
    components: {
      IhuSelectDropdown,
      IhuSelectList,
      IhuSelectTags,
    },
    directives: {
      clickOutside,
    },
    mixins: [currentThemeMixin],
    props: {
      type: {
        required: true,
        validator: type => includes(values(SELECT_TYPES), type),
      },
      selected: {
        type: Array,
        default: () => ([]),
      },
      placeholder: {
        type: String,
        default: 'Please Select',
      },
      placeholderIconClass: {
        type: String,
        default: '',
      },
      items: {
        type: Array,
        default: () => ([]),
      },
      // The field of the items key
      itemIdAttr: {
        type: String,
        default: 'id',
      },
      // The field of the item value
      itemValueAttr: {
        type: String,
        default: 'value',
      },
      // A custom function for the search.
      searchFunc: {
        type: Function,
        default: (items, searchTerm, itemValueAttr) =>
          filter(items, item => includes(item[itemValueAttr].toLowerCase().trim(), searchTerm.toLowerCase().trim())),
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      dropdownSearchable: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        required: false,
        default: '',
      },
      tooltip: {
        type: String,
        default: '',
      },
      // whiteList is used for isAll Comparison
      whiteList: {
        type: Array,
        default: () => ([]),
      },
    },
    data() {
      return {
        isListOpen: false,
        selectedItems: this.selected || [],
        searchTerm: '',
        icInfoDT,
        icInfoLT,
      };
    },
    computed: {
      isMultiSelect() {
        return this.type === SELECT_TYPES.MULTISELECT;
      },
      isSingleSelect() {
        return this.type === SELECT_TYPES.SINGLESELECT;
      },
      isDropdown() {
        return this.type === SELECT_TYPES.DROPDOWN;
      },
      filteredItems() {
        if (this.searchTerm) {
          return this.searchFunc(this.items, this.searchTerm, this.itemValueAttr);
        }
        return this.items;
      },
      isAllSelected() {
        const totalLength = this.selectedItems.length + this.whiteList.length;
        return !!this.items.length && this.items.length === totalLength;
      },
    },
    watch: {
      selected() {
        if (this.selected && this.selected.length === 0) {
          this.searchTerm = '';
        }
        this.selectedItems = this.selected;
      },
    },

    methods: {
      toggle(state) {
        if (!this.disabled) {
          this.isListOpen = state;
        }
        this.$emit('ihuSelectFocused');
      },
      setSelected(item) {
        this.decideActionOnItem(item);
        this.$emit('selectItems', this.selectedItems);
        this.searchTerm = '';
        if (!this.isMultiSelect) {
          this.hideList();
        }
      },
      disabledItemSelected(item) {
        this.$emit('disabledItemClicked', item);
      },
      searchTermInput(needle) {
        this.searchTerm = needle;
      },
      isItemSelected(item) {
        return !!this.selectedItems.find(i => i[this.itemIdAttr] === item[this.itemIdAttr]);
      },
      unselectItem(item) {
        this.selectedItems = this.selectedItems.filter(si => si[this.itemIdAttr] !== item[this.itemIdAttr]);
      },
      selectItem(item) {
        if (this.isMultiSelect) {
          this.selectedItems = [...this.selectedItems, { ...item }];
        } else {
          this.selectedItems = [{ ...item }];
        }
      },
      setAllSelected(selectAll) {
        if (selectAll) {
          const items = this.items.filter(({ disabled }) => !disabled);
          this.selectedItems = [...items];
        } else {
          this.selectedItems = [];
        }
        this.$emit('selectItems', this.selectedItems);
      },
      onClickOutside() {
        this.hideList();
      },

      hideList() {
        if (!this.isListOpen) {
          return;
        }

        this.isListOpen = false;
      },
      decideActionOnItem(item) {
        if (this.isItemSelected(item) && this.isMultiSelect) {
          this.unselectItem(item);
        } else {
          this.selectItem(item);
        }
      },
    },
  };
</script>

<style scoped lang="scss">
.ihu-select-container {
  display: inline-grid;
  label {
    display: flex;
    align-items: center;
    float: left;
    height: 14px;
    margin-left: 3px;
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 11.67px;
    line-height: 120%;
    text-align: justify;
    @include themify {
      color: getColor('medalliaSelfSetupItem', 'ihu');
    }
    flex: none;
    order: 0;
    flex-grow: 0;
    margin-bottom: 13px;
  }
  .info-icon {
    margin-left: 12px;
    height: 14px;
  }
  .select-wrapper {
    margin-bottom: 34px;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: start;
    align-items: stretch;
    position: relative;
  }
}
</style>
